/**
 * Shopify Helper Functions
 * 
 * These utilities help with common Shopify operations like formatting variant IDs,
 * handling cart operations, and managing product data.
 */

// Type definitions for better type safety
interface ProductVariant {
  id: string;
  title: string;
  price: {
    amount: string;
    currencyCode: string;
  };
  availableForSale: boolean;
  selectedOptions: Array<{
    name: string;
    value: string;
  }>;
}

/**
 * Finds the best default variant from an array of variants
 * Prioritizes available variants over unavailable ones
 * @param variants - Array of product variants
 * @returns The best default variant or null if no variants exist
 */
export const findDefaultVariant = (variants: ProductVariant[]): ProductVariant | null => {
  if (!variants.length) return null;
  
  // First, try to find an available variant
  const availableVariant = variants.find(variant => variant.availableForSale);
  
  // If we found an available variant, use it; otherwise fall back to the first variant
  return availableVariant || variants[0];
};

/**
 * Creates a selectedOptions object from a variant's selectedOptions
 * This is useful for initializing UI state to match a variant
 * @param variant - The product variant to extract options from
 * @returns Object mapping option names to their selected values
 */
export const createSelectedOptionsFromVariant = (variant: ProductVariant | null): Record<string, string> => {
  if (!variant) return {};
  
  const options: Record<string, string> = {};
  variant.selectedOptions.forEach(option => {
    options[option.name] = option.value;
  });
  
  return options;
};

/**
 * Formats a Shopify variant ID to the proper GraphQL format
 * @param variantId - The numeric variant ID or already formatted GID
 * @returns Properly formatted GraphQL variant ID
 */
export const formatVariantId = (variantId: string | number): string => {
  if (typeof variantId === 'string' && variantId.startsWith('gid://')) {
    return variantId;
  }
  return `gid://shopify/ProductVariant/${variantId}`;
};

/**
 * Extracts the numeric ID from a GraphQL variant ID
 * @param gid - The GraphQL variant ID
 * @returns The numeric variant ID
 */
export const extractVariantId = (gid: string): string => {
  if (gid.startsWith('gid://shopify/ProductVariant/')) {
    return gid.replace('gid://shopify/ProductVariant/', '');
  }
  return gid;
};

/**
 * Formats a price for display
 * @param amount - The price amount as a string
 * @param currencyCode - The currency code (e.g., 'USD', 'CAD')
 * @returns Formatted price string
 */
export const formatPrice = (amount: string, currencyCode: string = 'USD'): string => {
  const price = parseFloat(amount);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
  }).format(price);
};

/**
 * Creates a cart line item object for adding to cart
 * @param variantId - The product variant ID (will be formatted if needed)
 * @param quantity - The quantity to add
 * @param attributes - Optional attributes for the line item
 * @returns Cart line item object
 */
export const createCartLineItem = (
  variantId: string | number,
  quantity: number = 1,
  attributes: Array<{ key: string; value: string }> = []
) => ({
  merchandiseId: formatVariantId(variantId),
  quantity,
  ...(attributes.length > 0 && { attributes }),
});

/**
 * Validates if a string is a valid Shopify GraphQL ID
 * @param id - The ID to validate
 * @returns True if valid, false otherwise
 */
export const isValidShopifyGid = (id: string): boolean => {
  return id.startsWith('gid://shopify/') && id.length > 14;
};

/**
 * Gets product variant IDs from a product query result
 * @param product - The product object from a GraphQL query
 * @returns Array of variant IDs
 */
export const getVariantIds = (product: any): string[] => {
  if (!product?.variants?.edges) return [];
  
  return product.variants.edges
    .map((edge: any) => edge.node?.id)
    .filter((id: string) => id && isValidShopifyGid(id));
};

/**
 * Example usage and common patterns
 */
export const EXAMPLE_USAGE = {
  // Add a single item to cart
  addToCart: `
    const lineItem = createCartLineItem('12345678901234', 2);
    await linesAdd([lineItem]);
  `,
  
  // Add multiple items to cart
  addMultipleToCart: `
    const items = [
      createCartLineItem('12345678901234', 1),
      createCartLineItem('12345678901235', 2),
    ];
    await linesAdd(items);
  `,
  
  // Format a variant ID
  formatId: `
    const formattedId = formatVariantId('12345678901234');
    // Result: 'gid://shopify/ProductVariant/12345678901234'
  `,
  
  // Format a price
  formatPrice: `
    const price = formatPrice('29.99', 'USD');
    // Result: '$29.99'
  `,
}; 